import { useEffect, useState } from 'react'
import { api } from '../servizi/api.ts'
import { Bottone, Etichetta, Modale, Scelta, type TonoEtichetta } from '../componenti/base.tsx'
import { conteggio, Guscio, messaggioErrore, usaElenco } from '../componenti/elenco.tsx'

/**
 * Registro delle email di sistema.
 *
 * Mostra cosa il sistema ha scritto e a chi, senza mostrare a quale indirizzo: per le email di
 * servizio il destinatario e' l'operatore, per quelle partite da una scheda e' uno pseudonimo
 * calcolato sull'indice cieco dell'indirizzo. Due invii allo stesso indirizzo hanno lo stesso
 * pseudonimo, e da questo non si risale all'indirizzo.
 *
 * L'oggetto non compare: e' cifrato con la chiave della persona e per consultare il registro non
 * serve. I modelli si leggono in anteprima, con valori fittizi.
 */

type Riga = {
  id: string
  creata_il: string
  inviata_il: string | null
  modello: string | null
  modello_versione: number | null
  evento_generatore: string | null
  stato: string
  errore: string | null
  utente_destinatario_id: string | null
  nome: string | null
  cognome: string | null
  quanti_destinatari: number
  destinatario_pseudonimo: string | null
}

type Modello = {
  codice: string
  versione: number
  parametri: string[]
  oggetto: string
  testo: string
}

const EVENTI = [
  { valore: 'auth.magic_link', etichetta: 'Collegamento di accesso' },
  { valore: 'auth.otp', etichetta: 'Codice usa e getta' },
  { valore: 'auth.reimposta_password', etichetta: 'Reimpostazione della password' },
  { valore: 'auth.recupero_richiesto', etichetta: 'Avviso di richiesta di recupero della password' },
  { valore: 'auth.recupero_password', etichetta: 'Recupero della password approvato' },
  { valore: 'utente.email_recupero', etichetta: 'Codice per l’email di recupero' },
  { valore: 'utente.invito', etichetta: 'Invito di un utente' },
  { valore: 'notifica.riepilogo', etichetta: 'Riepilogo delle notifiche' },
  { valore: 'mediazione.assegnata', etichetta: 'Mediazione assegnata a un mediatore' },
  { valore: 'mediazione.spostata', etichetta: 'Mediazione spostata o modificata' },
  { valore: 'mediazione.annullata', etichetta: 'Mediazione annullata o tolta al mediatore' },
  { valore: 'scheda.email', etichetta: 'Email scritta da una scheda' },
]

const STATI = [
  { valore: 'in_coda', etichetta: 'In coda' },
  { valore: 'inviata', etichetta: 'Inviata' },
  { valore: 'consegnata', etichetta: 'Consegnata' },
  { valore: 'respinta', etichetta: 'Respinta' },
  { valore: 'errore', etichetta: 'Errore' },
]

const TONO: Record<string, TonoEtichetta> = {
  in_coda: 'neutro', inviata: 'informativo', consegnata: 'positivo',
  respinta: 'critico', errore: 'critico',
}

const quando = (v: string) => new Date(v).toLocaleString('it-IT')

export function RegistroEmail() {
  const [modello, setModello] = useState('')
  const [evento, setEvento] = useState('')
  const [stato, setStato] = useState('')
  const [modelli, setModelli] = useState<Modello[] | null>(null)
  const [erroreModelli, setErroreModelli] = useState<string | null>(null)
  const [anteprima, setAnteprima] = useState(false)

  const filtri = new URLSearchParams()
  if (modello) filtri.set('modello', modello)
  if (evento) filtri.set('evento', evento)
  if (stato) filtri.set('stato', stato)
  const query = filtri.toString()
  const { dati, errore, finestra } = usaElenco<Riga>(
    `/amministrazione/email${query ? `?${query}` : ''}`,
  )

  // I modelli servono sia al filtro sia all'anteprima: si leggono una volta sola.
  useEffect(() => {
    api.get<Modello[]>('/amministrazione/modelli-email')
      .then(setModelli)
      .catch((e) => setErroreModelli(messaggioErrore(e)))
  }, [])

  return (
    <>
      <Guscio
        titolo="Registro delle email"
        sottotitolo={conteggio(finestra.totale, 'email registrata', 'email registrate')
          ?? 'Email inviate dal sistema e dalle schede.'}
        errore={errore}
        dati={dati}
        finestra={finestra}
        vuoto="Nessuna email registrata."
        intestazioni={['Quando', 'Modello', 'Evento', 'Destinatario', 'Esito']}
        azioni={
          <div className="email-documenti-filtri">
            <Scelta etichetta="Modello" valore={modello} cambia={setModello} vuota="Tutti i modelli"
                    opzioni={(modelli ?? []).map((m) => ({ valore: m.codice, etichetta: m.codice }))} />
            <Scelta etichetta="Evento" valore={evento} cambia={setEvento} vuota="Tutti gli eventi"
                    opzioni={EVENTI} />
            <Scelta etichetta="Esito" valore={stato} cambia={setStato} vuota="Tutti gli esiti"
                    opzioni={STATI} />
            <Bottone onClick={() => setAnteprima(true)} disabled={!modelli?.length}>
              Modelli e anteprima
            </Bottone>
          </div>
        }
      >
        {dati?.map((r) => (
          <tr key={r.id}>
            <td data-etichetta="Quando" className="piccolo">{quando(r.creata_il)}</td>
            <td data-etichetta="Modello">
              {r.modello
                ? <>{r.modello} <span className="tenue piccolo">v{r.modello_versione}</span></>
                : <span className="tenue">dalla scheda</span>}
            </td>
            <td data-etichetta="Evento" className="piccolo">
              {EVENTI.find((e) => e.valore === r.evento_generatore)?.etichetta
                ?? r.evento_generatore ?? '—'}
            </td>
            <td data-etichetta="Destinatario">
              {r.utente_destinatario_id
                ? `${r.cognome ?? ''} ${r.nome ?? ''}`.trim()
                : (
                  <>
                    <span className="email-documenti-pseudonimo">
                      {r.destinatario_pseudonimo ?? '—'}
                    </span>
                    {r.quanti_destinatari > 1 && (
                      <span className="tenue piccolo"> +{r.quanti_destinatari - 1}</span>
                    )}
                  </>
                )}
            </td>
            <td data-etichetta="Esito">
              <Etichetta tono={TONO[r.stato] ?? 'neutro'}>{r.stato}</Etichetta>
              {r.errore && <span className="tenue piccolo"> {r.errore}</span>}
            </td>
          </tr>
        ))}
      </Guscio>

      {anteprima && (
        <Modale titolo="Modelli delle email di sistema" larga chiudi={() => setAnteprima(false)}>
          <p className="piccolo tenue">
            I modelli stanno nel codice e sono versionati nel repository. Il registro delle
            revisioni, con la data e chi ha approvato ogni versione, è in
            {' '}<code>docs/modelli-email.md</code>. L’anteprima usa valori fittizi.
          </p>
          {erroreModelli && <p className="campo-errore" role="alert">{erroreModelli}</p>}
          {modelli?.map((m) => (
            <section key={m.codice} className="email-documenti-modello">
              <h3>{m.codice} <Etichetta tono="neutro">v{m.versione}</Etichetta></h3>
              <p className="piccolo tenue">Parametri ammessi: {m.parametri.join(', ')}</p>
              <p><strong>Oggetto:</strong> {m.oggetto}</p>
              <pre className="email-documenti-anteprima">{m.testo}</pre>
            </section>
          ))}
        </Modale>
      )}
    </>
  )
}
